import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PostCard } from "@/components/PostCard";
import { AdUnit } from "@/components/AdUnit";
import { getPostBySlug, getRelatedPosts, type PostSection } from "@/data/posts";
import { getReviewedProduct } from "@/data/reviewed-products";
import { getReviewsForPost } from "@/data/reviews";
import { siteConfig, categorySlug } from "@/config";
import { sizedImage } from "@/lib/image";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/post/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found — Gooobus" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `${siteConfig.domain}/post/${params.slug}`;
    const image = sizedImage(post.thumbnail, 1200);
    const productMeta = getReviewedProduct(post.id);
    const revs = getReviewsForPost(post.id);
    const faqSections = post.body.filter(
      (s): s is Extract<PostSection, { type: "faq" }> => s.type === "faq",
    );

    const scripts: Array<Record<string, unknown>> = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          image: [image],
          datePublished: post.publishDate,
          dateModified: post.publishDate,
          mainEntityOfPage: url,
          author: {
            "@type": "Person",
            name: post.author,
            worksFor: {
              "@type": "Organization",
              name: siteConfig.siteName,
              url: siteConfig.domain,
            },
          },
          publisher: {
            "@type": "Organization",
            name: siteConfig.siteName,
            logo: {
              "@type": "ImageObject",
              url: `${siteConfig.domain}/favicon.png`,
            },
          },
          keywords: post.keywords.join(", "),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.domain },
            {
              "@type": "ListItem",
              position: 2,
              name: post.category,
              item: `${siteConfig.domain}/category/${categorySlug(post.category)}`,
            },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        }),
      },
    ];

    if (faqSections.length) {
      const mainEntity = faqSections.flatMap((f) =>
        f.items.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      );
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity,
        }),
      });
    }

    if (productMeta && revs.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: productMeta.name,
          applicationCategory: productMeta.applicationCategory,
          operatingSystem: productMeta.operatingSystem,
          url: productMeta.url,
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5",
            reviewCount: String(revs.length),
            bestRating: "5",
            worstRating: "1",
          },
          review: revs.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            datePublished: r.date,
            reviewBody: r.comment,
            reviewRating: {
              "@type": "Rating",
              ratingValue: "5",
              bestRating: "5",
              worstRating: "1",
            },
          })),
        }),
      });
    }

    return {
      meta: [
        { title: `${post.title} | ${siteConfig.siteName}` },
        { name: "description", content: post.description },
        { name: "keywords", content: post.keywords.join(", ") },
        { property: "og:type", content: "article" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.description },
        { name: "twitter:image", content: image },
        { property: "article:published_time", content: post.publishDate },
        { property: "article:author", content: post.author },
        { property: "article:section", content: post.category },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: PostPage,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-3xl font-bold">Article not found</h1>
        <p className="mt-2 text-muted-foreground">
          This article may have been moved or renamed.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Back home
        </Link>
      </div>
    </div>
  ),
  errorComponent: PostError,
});

function PostError({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Something broke loading this article.</h1>
        <button
          onClick={() => reset()}
          className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

function renderSection(section: PostSection, key: number, h2Counter: { n: number }) {
  switch (section.type) {
    case "p":
      return <p key={key}>{section.text}</p>;
    case "h2": {
      h2Counter.n += 1;
      const inArticle =
        h2Counter.n === 2 && siteConfig.adSlots.postInArticle1 ? (
          <AdUnit
            key={`${key}-ad`}
            slot={siteConfig.adSlots.postInArticle1}
            variant="inarticle"
            minHeight={250}
            className="my-6"
          />
        ) : null;
      return (
        <>
          {inArticle}
          <h2 key={key}>{section.text}</h2>
        </>
      );
    }
    case "h3":
      return <h3 key={key}>{section.text}</h3>;
    case "ul":
      return (
        <ul key={key}>
          {section.items.map((i, idx) => (
            <li key={idx}>{i}</li>
          ))}
        </ul>
      );
    case "quote":
      return <blockquote key={key}>{section.text}</blockquote>;
    case "table":
      return (
        <div key={key} className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                {section.headers.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((c, j) => (
                    <td key={j}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "faq":
      return (
        <div key={key} className="my-6 space-y-4">
          {section.items.map((it, i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-4">
              <h3 className="mt-0 mb-2 text-lg font-semibold">{it.q}</h3>
              <p className="mb-0">{it.a}</p>
            </div>
          ))}
        </div>
      );
  }
}

function PostPage() {
  const data = Route.useLoaderData() as { post: import("@/data/posts").Post };
  const post = data.post;
  const related = getRelatedPosts(post.slug, post.category);
  const reviews = getReviewsForPost(post.id);
  const heroLarge = sizedImage(post.thumbnail, 1600);
  const h2Counter = { n: 0 };


  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li>
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link
                to="/category/$category"
                params={{ category: categorySlug(post.category) }}
                className="hover:text-foreground"
              >
                {post.category}
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-foreground">{post.title}</li>
          </ol>
        </nav>

        {siteConfig.adSlots.postTopNative && (
          <AdUnit
            slot={siteConfig.adSlots.postTopNative}
            variant="display"
            minHeight={120}
            className="mb-6"
          />
        )}

        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{post.title}</h1>
        <p className="mt-3 text-lg text-muted-foreground">{post.description}</p>

        <div className="mt-5 flex items-center gap-3 text-sm text-muted-foreground">
          <img
            src="/admin-avatar.webp"
            alt=""
            width={20}
            height={20}
            loading="lazy"
            decoding="async"
            className="h-5 w-5 rounded-full object-cover"
          />
          <span>{post.author}</span>
          <span aria-hidden="true">·</span>
          <time dateTime={post.publishDate}>{formatDate(post.publishDate)}</time>
          <span aria-hidden="true">·</span>
          <Link
            to="/category/$category"
            params={{ category: categorySlug(post.category) }}
            className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
          >
            {post.category}
          </Link>
        </div>

        {siteConfig.adSlots.postMultiplex && (
          <AdUnit
            slot={siteConfig.adSlots.postMultiplex}
            variant="multiplex"
            minHeight={220}
            className="my-6"
          />
        )}

        <div className="my-8 overflow-hidden rounded-xl bg-muted">
          <img
            src={heroLarge}
            alt={post.title}
            width={1600}
            height={900}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            sizes="(min-width: 1024px) 768px, 100vw"
            className="h-auto w-full object-cover"
          />
        </div>

        <article className="prose-article">
          {post.body.map((s: PostSection, i: number) => renderSection(s, i, h2Counter))}
        </article>

        {reviews.length > 0 && (
          <section className="mt-14">
            <h2 className="text-2xl font-bold tracking-tight">Reader reviews</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              What our readers say about this article.
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {reviews.map((r, i) => (
                <div key={i} className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{r.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.role} · {r.location}
                      </div>
                    </div>
                    <div aria-label="5 out of 5 stars" className="text-primary">
                      ★★★★★
                    </div>
                  </div>
                  <p className="mt-3 text-sm">{r.comment}</p>
                  <time
                    dateTime={r.date}
                    className="mt-2 block text-xs text-muted-foreground"
                  >
                    {formatDate(r.date)}
                  </time>
                </div>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14">
            <h2 className="text-2xl font-bold tracking-tight">Related articles</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          </section>
        )}

      </main>
      <Footer />
    </div>
  );
}
